import { useState } from "react";

interface KeptRows<TRows> {
    rows: TRows;
    datasetKey: string;
}

interface UseKeptRowsParams<TRows> {
    /** The current request's rows; `null` until every request they need answered. Keep it referentially stable. */
    rows: TRows | null;
    /** Which data the rows are: changes for another client, entity, task or tab, not for filters, search, sort or page. */
    datasetKey: string;
}

/**
 * What a grid shows while its rows load: the last answered rows of the same
 * dataset, marked refetching, so a filter, search, sort or page change keeps
 * them on screen until the new rows arrive. Another dataset starts from the
 * loading state (`rows: null`) instead of showing the previous one's rows.
 */
export const useKeptRows = <TRows>({
    rows,
    datasetKey,
}: UseKeptRowsParams<TRows>) => {
    const [kept, setKept] = useState<KeptRows<TRows> | null>(null);
    if (
        rows !== null &&
        (kept?.rows !== rows || kept.datasetKey !== datasetKey)
    ) {
        setKept({ rows, datasetKey });
    }
    const shownRows =
        rows ?? (kept?.datasetKey === datasetKey ? kept.rows : null);
    return {
        rows: shownRows,
        isRefetching: rows === null && shownRows !== null,
    };
};
